<template>
    <!-- Kept in the tree rather than switched in and out, so that the sheet can travel
         both ways; `inert` is what keeps a shut sheet out of the tab order and away from
         a screen reader, which a transform alone does not. -->
    <div class="milo-scrim" :class="{ on: chatStore.open }" @click="close"></div>

    <div
        id="milo-sheet"
        ref="sheet"
        class="milo-sheet"
        :class="{ on: chatStore.open }"
        role="dialog"
        aria-modal="true"
        aria-labelledby="milo-sheet-title"
        :inert="!chatStore.open"
        :aria-hidden="!chatStore.open"
        :style="dragStyle"
        @keydown.esc.prevent="close"
        @keydown.tab="trap"
    >
        <!-- The handle, and the whole of drag-to-dismiss. A thumb on a telephone pushes
             a sheet away; it does not look for a close button first. -->
        <div
            class="milo-grip"
            @pointerdown="grab"
            @pointermove="drag"
            @pointerup="release"
            @pointercancel="release"
            @lostpointercapture="release"
        ><span></span></div>

        <div class="milo-top">
            <h2 id="milo-sheet-title">{{ $t('milo.title') }}</h2>
            <button class="milo-close" type="button" :aria-label="$t('milo.close')" @click="close">✕</button>
        </div>

        <MiloConversation ref="conversation" />
    </div>
</template>

<script setup lang="ts">
    import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'

    import MiloConversation from '@/components/MiloConversation.vue'
    import { useChatStore } from '@/stores/chat'

    /**
     * The bottom sheet Milo lives in.
     *
     * Hand-written rather than a `USlideover`, for the reason written at the foot of
     * `assets/main.css`: this portal has no Tailwind and no Nuxt UI, and bringing them in
     * for one sheet would land a preflight and a theme on top of a stylesheet that is the
     * agreed design. What a slideover would have given for nothing is here on purpose —
     * the modal role, the focus trap, Escape, and the page behind not scrolling.
     */

    /** How far down the sheet has to be pushed before letting go shuts it. Ninety
     *  pixels: far enough that a scroll that starts on the grip is not a dismissal,
     *  near enough that the gesture never feels like work. */
    const DISMISS_AFTER = 90

    const chatStore = useChatStore()

    const sheet        = ref<HTMLElement | null>(null)
    const conversation = ref<InstanceType<typeof MiloConversation> | null>(null)

    /** How far the thumb has pushed the sheet down, in pixels. Zero when nobody is
     *  dragging, which is also when the CSS transition is allowed to do the moving. */
    const dragged = ref(0)
    let from: number | null = null

    const dragStyle = computed(() => (dragged.value
        ? { transform: `translateY(${dragged.value}px)`, transition: 'none' }
        : {}))

    const close = () => {
        dragged.value = 0
        chatStore.closeSheet()
    }

    const grab = (event: PointerEvent) => {
        from = event.clientY

        // Capture so that a thumb which wanders off the 36-pixel handle still reports
        // where it went, and — more to the point — still reports letting go. A browser
        // that refuses it leaves the gesture working within the handle, which is where
        // it starts; a sheet stuck half-way because nothing told us the thumb had lifted
        // is the failure worth guarding against.
        try {
            (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId)
        } catch {
            // Nothing to do: the drag simply has no capture.
        }
    }

    const drag = (event: PointerEvent) => {
        if (from === null) return

        // Downwards only: a sheet dragged up would be a sheet that grows, and this one
        // is as tall as the screen allows already.
        dragged.value = Math.max(0, event.clientY - from)
    }

    const release = () => {
        if (from === null) return

        const far = dragged.value >= DISMISS_AFTER

        from          = null
        dragged.value = 0

        if (far) close()
    }

    /**
     * Keeps Tab inside the sheet while it is open.
     *
     * The list is read at the moment of the press rather than kept: the cards come and
     * go with the conversation, and a trap holding yesterday's buttons would send the
     * cursor to a card that is no longer on screen.
     */
    const trap = (event: KeyboardEvent) => {
        const focusable = [...(sheet.value?.querySelectorAll<HTMLElement>(
            'button:not([disabled]), textarea:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])',
        ) ?? [])]

        if (!focusable.length) return

        const first = focusable[0]!
        const last  = focusable[focusable.length - 1]!

        if (event.shiftKey && document.activeElement === first) {
            event.preventDefault()
            last.focus()
        } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault()
            first.focus()
        }
    }

    /** What had the cursor before the sheet took it — the button, in every real case. */
    let opener: HTMLElement | null = null

    /** The page behind must not scroll under an open sheet. Restored exactly, because
     *  the portal never sets it itself and an empty string is what "not set" is. */
    const lockPage = (locked: boolean) => {
        document.body.style.overflow = locked ? 'hidden' : ''
    }

    watch(() => chatStore.open, async open => {
        lockPage(open)

        if (open) {
            opener = document.activeElement as HTMLElement | null

            await nextTick()
            conversation.value?.focus()
            await conversation.value?.scrollDown()

            return
        }

        // Back where it came from — and where it came from is the button, whether or not
        // the browser said so. Safari on iOS does not focus a <button> when it is
        // tapped, so `document.activeElement` at the moment of opening is the BODY as
        // often as not; returning the cursor there would leave a keyboard user at the
        // top of the page, which is exactly what this is for.
        const back = opener && opener !== document.body
            ? opener
            : document.querySelector<HTMLElement>('.milo-fab')

        back?.focus()
        opener = null
    })

    onBeforeUnmount(() => lockPage(false))
</script>
